import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Creator, login as apiLogin, register as apiRegister, getProfile } from '../services/api';

interface AuthContextType {
  creator: Creator | null;
  token: string | null;
  loading: boolean;
  isProfileComplete: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (alias: string, email: string, password: string, acceptedTerms: boolean) => Promise<void>;
  logout: () => void;
  refreshProfileStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [creator, setCreator] = useState<Creator | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isProfileComplete, setIsProfileComplete] = useState(false);

  useEffect(() => {
    const safeParseCreator = (value: string | null) => {
      if (!value) return null;
      try {
        return JSON.parse(value) as Creator;
      } catch {
        return null;
      }
    };

    // Clean up old 'user' key from localStorage (migration from user to creator)
    if (localStorage.getItem('user')) {
      localStorage.removeItem('user');
    }
    
    // Check for existing session
    const storedToken = localStorage.getItem('token');
    const storedCreator = localStorage.getItem('creator');
    const storedProfileComplete = localStorage.getItem('isProfileComplete');

    if (storedToken && storedCreator) {
      const parsedCreator = safeParseCreator(storedCreator);
      if (!parsedCreator) {
        localStorage.removeItem('token');
        localStorage.removeItem('creator');
        localStorage.removeItem('isProfileComplete');
        setToken(null);
        setCreator(null);
        setIsProfileComplete(false);
        setLoading(false);
        return;
      }

      setToken(storedToken);
      setCreator(parsedCreator);
      setIsProfileComplete(storedProfileComplete === 'true');
      
      // Validate token and refresh profile status
      getProfile()
        .then((response) => {
          setCreator(response.data.creator);
          setIsProfileComplete(response.data.isProfileComplete);
          localStorage.setItem('creator', JSON.stringify(response.data.creator));
          localStorage.setItem('isProfileComplete', String(response.data.isProfileComplete));
        })
        .catch(() => {
          // Token invalid, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('creator');
          localStorage.removeItem('isProfileComplete');
          setToken(null);
          setCreator(null);
          setIsProfileComplete(false);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    const response = await apiLogin({ email, password });
    const { token: newToken, creator: newCreator, isProfileComplete: profileComplete } = response.data;

    setToken(newToken);
    setCreator(newCreator);
    setIsProfileComplete(profileComplete);
    localStorage.setItem('token', newToken);
    localStorage.setItem('creator', JSON.stringify(newCreator));
    localStorage.setItem('isProfileComplete', String(profileComplete));
  };

  const register = async (alias: string, email: string, password: string, acceptedTerms: boolean) => {
    // Registration now requires email verification, so we don't auto-login
    await apiRegister({ alias, email, password, acceptedTerms });
    // User will be redirected to check email and verify before logging in
  };

  const logout = () => {
    setToken(null);
    setCreator(null);
    setIsProfileComplete(false);
    localStorage.removeItem('token');
    localStorage.removeItem('creator');
    localStorage.removeItem('isProfileComplete');
  };

  const refreshProfileStatus = async () => {
    try {
      const response = await getProfile();
      setCreator(response.data.creator);
      setIsProfileComplete(response.data.isProfileComplete);
      localStorage.setItem('creator', JSON.stringify(response.data.creator));
      localStorage.setItem('isProfileComplete', String(response.data.isProfileComplete));
    } catch (error) {
      console.error('Failed to refresh profile status:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ creator, token, loading, isProfileComplete, login, register, logout, refreshProfileStatus }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
